import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Button from "../components/UI/Button";
import Card from "../components/UI/Card";
import BookingCheckForm from "../components/Booking/BookingCheckForm";

function HomeScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Carousel images
  const carouselImages = [
    "/assets/images/villa Baidane-30.jpg",
    "/assets/images/DSCN1766-Enhanced-NR 2.jpg",
    "/assets/images/DSCN1674.jpg",
    "/assets/images/villa Baidane-18.jpg",
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZooming, setIsZooming] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Mosaic modal items (home gallery tiles)
  const mosaicItems = [
    { src: "/assets/images/DSCN1665.jpg", title: "RETREATS" },
    { src: "/assets/images/DSCN1753.jpg", title: "SPA & POOLS" },
    { src: "/assets/images/villa Baidane-16.jpg", title: "INTERIOR STYLING" },
    { src: "/assets/images/villa Baidane-63.jpg", title: "PRIVATE GARDENS" },
    { src: "/assets/images/DSCN1733.jpg", title: "TRADITIONAL\nARCHITECTURE" },
    { src: "/assets/images/DSCN1670.jpg", title: "GASTRONOMY" },
    { src: "/assets/images/villa Baidane-36.jpg", title: "LUXURY HOSPITALITY" },
    { src: "/assets/images/villa Baidane-02.jpg", title: "WELLNESS & SPA" }
  ];

  const [isMosaicOpen, setIsMosaicOpen] = useState(false);
  const [mosaicIndex, setMosaicIndex] = useState(0);

  const openMosaic = (idx) => {
    setMosaicIndex(idx);
    setIsMosaicOpen(true);
  };
  const closeMosaic = () => setIsMosaicOpen(false);
  const prevMosaic = () => setMosaicIndex((prev) => (prev - 1 + mosaicItems.length) % mosaicItems.length);
  const nextMosaic = () => setMosaicIndex((prev) => (prev + 1) % mosaicItems.length);

  // Keyboard controls for modal
  useEffect(() => {
    if (!isMosaicOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeMosaic();
      if (e.key === 'ArrowLeft') prevMosaic();
      if (e.key === 'ArrowRight') nextMosaic();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMosaicOpen]);

  // Auto-advance carousel
  useEffect(() => {
    if (isPaused) return;

    let zoomTimer;
    let changeTimer;

    const startCycle = () => {
      // Start zoom
      setIsZooming(true);
      console.log("Starting zoom animation");

      // After 5 seconds, change image
      changeTimer = setTimeout(() => {
        setCurrentImageIndex((prevIndex) => {
          const nextIndex =
            prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1;
          console.log("Changing image from", prevIndex, "to", nextIndex);
          return nextIndex;
        });
        setIsZooming(false);
        console.log("Stopping zoom animation");
      }, 5000);
    };

    // Start immediately
    const initialTimer = setTimeout(startCycle, 500);

    // Set up interval for subsequent cycles
    const interval = setInterval(startCycle, 7000);

    return () => {
      clearInterval(interval);
      clearTimeout(initialTimer);
      clearTimeout(zoomTimer);
      clearTimeout(changeTimer);
    };
  }, [carouselImages.length, isPaused]);

  // Navigation functions
  const goToPrevious = () => {
    setIsZooming(true);
    setTimeout(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === 0 ? carouselImages.length - 1 : prevIndex - 1
      );
      setIsZooming(false);
    }, 1000); // 1 second zoom for manual navigation
  };

  const goToNext = () => {
    setIsZooming(true);
    setTimeout(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
      setIsZooming(false);
    }, 1000); // 1 second zoom for manual navigation
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: 'url("/assets/icons/top-img-bg.png") ' }}
    >
      {/* Hero Section */}
      <section
        className="relative h-screen flex items-center justify-center overflow-hidden"
        // onMouseEnter={() => setIsPaused(true)}
        // onMouseLeave={() => setIsPaused(false)}
      >
        {/* Carousel Background */}
        <div className="absolute inset-0 carousel-container">
          {carouselImages.map((image, index) => (
            <div
              key={index}
              className={`carousel-image absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000 ${
                index === currentImageIndex ? "opacity-100" : "opacity-0"
              }`}
              style={{
                backgroundImage: `url('${image}')`,
                transform: `scale(${
                  index === currentImageIndex
                    ? isZooming
                      ? "1.15"
                      : "1.0"
                    : "1.0"
                })`,
                transition:
                  index === currentImageIndex
                    ? "transform 5s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 1s ease-in-out"
                    : "opacity 1s ease-in-out",
              }}
            />
          ))}
        </div>

        {/* Overlay */}
        <div className="absolute inset-0 bg-black opacity-50"></div>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all duration-300 hover:scale-110"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>

        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all duration-300 hover:scale-110"
          aria-label="Next image"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>

        {/* Carousel Indicators */}

        <div className="relative z-10 text-center text-white px-4">
          <img
            src="/assets/icons/logo-negativ-vb.png"
            alt="Village Baidane"
            className="h-48 w-auto mb-8 mx-auto"
          />
          <h1 className="text-5xl md:text-7xl font-sofia font-bold mb-6 animate-fade-in">
            {t("hero.title")}
          </h1>
          <p className="text-xl md:text-2xl font-sofia font-light mb-8 max-w-3xl mx-auto animate-slide-up">
            {t("hero.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
            <Button
              size="lg"
              className="bg-[#81736d] hover:bg-[#81736d] hover:shadow-glow"
              onClick={() => navigate("/our-villas")}
            >
              {t("common.exploreVillas")}
            </Button>
            {/* <Button
              variant="outline"
              size="lg"
              className="border-2 border-white text-white hover:bg-white hover:text-primary backdrop-blur-sm bg-white/10"
              onClick={() => navigate('/booking')}
            >
              {t('common.bookNow')}
            </Button> */}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </div>
      </section>

      {/* Booking Check Form */}
      <section className="relative -mt-20 z-30 px-4 ">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 lg:p-8 border border-white/20">
            <h3 className="text-2xl font-sofia font-bold text-[#81736d] text-center mb-6">
              {t("booking.checkAvailability")}
            </h3>

            <BookingCheckForm />
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-16 ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-sofia font-bold text-black mb-4">
              {t("home.welcome.title")}
            </h2>
            <p className="text-lg font-sofia text-black max-w-3xl mx-auto">
              {t("home.welcome.description")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card hover className="text-center">
              <div className="w-16 h-16 bg-[#81736d] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-sofia font-bold text-[#81736d] mb-2">
                {t("home.features.luxuryVillas.title")}
              </h3>
              <p className="text-black font-sofia">
                {t("home.features.luxuryVillas.description")}
              </p>
            </Card>

            <Card hover className="text-center">
              <div className="w-16 h-16 bg-[#81736d] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-sofia font-bold text-[#81736d] mb-2">
                {t("home.features.perfectLocation.title")}
              </h3>
              <p className="text-black font-sofia">
                {t("home.features.perfectLocation.description")}
              </p>
            </Card>

            <Card hover className="text-center">
              <div className="w-16 h-16 bg-[#81736d] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-sofia font-bold text-[#81736d] mb-2">
                {t("home.features.authenticExperience.title")}
              </h3>
              <p className="text-black font-sofia">
                {t("home.features.authenticExperience.description")}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* gallery mode */}
      <section className="bg-white py-8">
        <div className="mx-auto px-4">
          {/* Masonry Grid with Different Sizes */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px]">
            {/* RETREATS - Large */}
            <div className="relative col-span-2 row-span-2 overflow-hidden group cursor-pointer" onClick={() => openMosaic(0)}>
              <img
                src="/assets/images/DSCN1665.jpg"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                alt="Retreats"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center transition-all duration-300 group-hover:bg-black/30">
                <h2 className="text-white text-sm font-semibold text-center px-2 font-sofia">
                  RETREATS
                </h2>
              </div>
            </div>

            {/* SPA & POOLS - Small */}
            <div className="relative col-span-1 row-span-1 overflow-hidden group cursor-pointer" onClick={() => openMosaic(1)}>
              <img
                src="/assets/images/DSCN1753.jpg"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                alt="Spa & Pools"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center transition-all duration-300 group-hover:bg-black/30">
                <h2 className="text-white text-sm font-semibold text-center px-2 font-sofia">
                  SPA & POOLS
                </h2>
              </div>
            </div>

            {/* INTERIOR STYLING - Medium */}
            <div className="relative col-span-1 md:col-span-1 row-span-1 overflow-hidden group cursor-pointer" onClick={() => openMosaic(2)}>
              <img
                src="/assets/images/villa Baidane-16.jpg"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                alt="Interior Styling"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center transition-all duration-300 group-hover:bg-black/30">
                <h2 className="text-white text-sm font-semibold text-center px-2 font-sofia">
                  INTERIOR STYLING
                </h2>
              </div>
            </div>

            {/* GARDEN OASIS - Wide */}
            <div className="relative col-span-2 row-span-1 overflow-hidden group cursor-pointer" onClick={() => openMosaic(3)}>
              <img
                src="/assets/images/villa Baidane-63.jpg"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                alt="Garden Oasis"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center transition-all duration-300 group-hover:bg-black/30">
                <h2 className="text-white text-sm font-semibold text-center px-2 font-sofia">
                  PRIVATE GARDENS
                </h2>
              </div>
            </div>

            {/* TRADITIONAL ARCHITECTURE - Tall */}
            <div className="relative col-span-1 md:col-span-1 row-span-2 overflow-hidden group cursor-pointer" onClick={() => openMosaic(4)}>
              <img
                src="/assets/images/DSCN1733.jpg"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                alt="Traditional Architecture"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center transition-all duration-300 group-hover:bg-black/30">
                <h2 className="text-white text-sm font-semibold text-center px-2 font-sofia">
                  TRADITIONAL
                  <br />
                  ARCHITECTURE
                </h2>
              </div>
            </div>

            {/* GASTRONOMY - Small */}
            <div className="relative col-span-1 row-span-1 overflow-hidden group cursor-pointer" onClick={() => openMosaic(5)}>
              <img
                src="/assets/images/DSCN1670.jpg"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                alt="Gastronomy"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center transition-all duration-300 group-hover:bg-black/30">
                <h2 className="text-white text-sm font-semibold text-center px-2 font-sofia">

                  GASTRONOMY
                </h2>
              </div>
            </div>

            {/* LUXURY HOSPITALITY - Large */}
            <div className="relative col-span-1 md:col-span-2 md:row-span-2 row-span-1 overflow-hidden group cursor-pointer" onClick={() => openMosaic(6)}>
              <img
                src="/assets/images/villa Baidane-36.jpg"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                alt="Luxury Hospitality"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center transition-all duration-300 group-hover:bg-black/30">
                <h2 className="text-white text-xl font-semibold text-center px-4 font-sofia">
                  LUXURY HOSPITALITY
                </h2>
              </div>
            </div>

            {/* WELLNESS & SPA - Small */}
            <div className="relative md:col-span-1 col-span-2 row-span-1 overflow-hidden group cursor-pointer" onClick={() => openMosaic(7)}>
              <img
                src="/assets/images/villa Baidane-02.jpg"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                alt="Wellness & Spa"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center transition-all duration-300 group-hover:bg-black/30">
                <h2 className="text-white text-base font-semibold text-center px-2 font-sofia">
                  WELLNESS & SPA
                </h2>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Villa Showcase */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-sofia font-bold text-black mb-4">
              {t("home.villas.title")}
            </h2>
            <p className="text-lg font-sofia text-black max-w-3xl mx-auto">
              {t("home.villas.description")}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Villa Najat */}
            <Card className="overflow-hidden">
              <div className="relative h-64 mb-6">
                <img
                  src="/assets/images/villa Baidane-15.jpg"
                  alt={t("villas.najat.title")}
                  className="w-full h-full object-cover rounded-lg"
                />
                <div className="absolute top-4 left-4 bg-[#81736d] text-white px-3 py-1 rounded-full text-sm font-sofia font-medium">
                  {t("home.villas.availableNow")}
                </div>
              </div>
              <h3 className="text-2xl font-sofia font-bold text-[#81736d] mb-3">
                {t("villas.najat.title")}
              </h3>
              <p className="text-black font-sofia mb-4">
                {t("home.villas.najat.description")}
              </p>
              <ul className="text-sm font-sofia text-black mb-6 space-y-1">
                {t("home.villas.najat.features", { returnObjects: true }).map(
                  (feature, index) => (
                    <li key={index}>• {feature}</li>
                  )
                )}
              </ul>
              <div className="text-center">
                <Button
                  className="w-full"
                  onClick={() => navigate("/villa-najat")}
                >
                  {t("home.villas.viewDetails")}
                </Button>
              </div>
            </Card>

            {/* Villa Nadia */}
            <Card className="overflow-hidden">
              <div className="relative h-64 mb-6">
                <img
                  src="/assets/images/villa-nadia/villa-nadia-00001.webp"
                  alt={t("villas.nadia.title")}
                  className="w-full h-full object-cover rounded-lg"
                />
                <div className="absolute top-4 left-4 bg-[#81736d] text-white px-3 py-1 rounded-full text-sm font-sofia font-medium">
                  {t("villas.nadia.comingSoonBadge")}
                </div>
              </div>
              <h3 className="text-2xl font-sofia font-bold text-[#81736d] mb-3">
                {t("villas.nadia.title")}
              </h3>
              <p className="text-black font-sofia mb-4">
                {t("home.villas.nadia.description")}
              </p>
              <ul className="text-sm font-sofia text-black mb-6 space-y-1">
                {t("home.villas.nadia.features", { returnObjects: true }).map(
                  (feature, index) => (
                    <li key={index}>• {feature}</li>
                  )
                )}
              </ul>
              <div className="text-center">
                {/* <p className="text-sm font-sofia text-text-light mb-4">
                  {t('home.villas.nadia.availableFrom')}
                </p> */}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/villa-nadia")}
                >
                  {t("home.villas.viewDetails")}
                </Button>
              </div>
            </Card>
          </div>

          <div className="text-center mt-10">
            <Button
              size="lg"
              className="bg-[#81736d] hover:bg-[#81736d]"
              onClick={() => navigate("/our-villas")}
            >
              {t("ourVillas.viewAllVillas")}
            </Button>
          </div>
        </div>
      </section>

      {/* Location & Features */}
      <section className="py-16 ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-sofia font-bold text-black mb-6">
                {t("about.location.title")}
              </h2>
              <p className="text-lg font-sofia text-black mb-6">
                {t("about.description")}
              </p>

              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-[#81736d] rounded-full flex items-center justify-center mr-3">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <span className="font-sofia text-text-primary">
                    {t("about.location.fromAirport")}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-[#81736d] rounded-full flex items-center justify-center mr-3">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <span className="font-sofia text-text-primary">
                    {t("about.location.fromCenter")}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-[#81736d] rounded-full flex items-center justify-center mr-3">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </div>
                  <span className="font-sofia text-text-primary">
                    {t("about.location.property")}
                  </span>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="/assets/images/villa Baidane-30.jpg"
                alt="Villa exterior view"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-sofia font-bold text-black mb-4">
              {t("home.testimonials.title")}
            </h2>
            <p className="text-lg font-sofia text-black">
              {t("home.testimonials.description")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="text-center flex flex-col justify-between h-full">
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    className="w-5 h-5 text-[#81736d]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-black font-sofia italic mb-4">
                {t("home.testimonials.testimonial1.text")}
              </p>
              <div className="font-sofia font-semibold text-[#81736d]">
                {t("home.testimonials.testimonial1.name")}
              </div>
              <div className="font-sofia text-sm text-text-light">
                {t("home.testimonials.testimonial1.source")}
              </div>
            </Card>

            <Card className="text-center flex flex-col justify-between h-full">
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    className="w-5 h-5 text-[#81736d]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-black font-sofia italic mb-4">
                {t("home.testimonials.testimonial2.text")}
              </p>
              <div className="font-sofia font-semibold text-[#81736d]">
                {t("home.testimonials.testimonial2.name")}
              </div>
              <div className="font-sofia text-sm text-text-light">
                {t("home.testimonials.testimonial2.source")}
              </div>
            </Card>

            <Card className="text-center flex flex-col justify-between h-full">
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    className="w-5 h-5 text-[#81736d]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-black font-sofia italic mb-4">
                {t("home.testimonials.testimonial3.text")}
              </p>
              <div className="font-sofia font-semibold text-[#81736d]">
                {t("home.testimonials.testimonial3.name")}
              </div>
              <div className="font-sofia text-sm text-text-light">
                {t("home.testimonials.testimonial3.source")}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing & Booking CTA */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{
            backgroundImage: `url('/assets/images/villa Baidane-05.jpg')`,
          }}
        ></div>
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-50 "></div>
        {/* <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-accent/90"></div> */}

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-sofia font-bold mb-6">
            {t("home.cta.title")}
          </h2>
          <p className="text-xl font-sofia font-light mb-8 max-w-3xl mx-auto">
            {t("home.cta.description")}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center bg-[#81736d]/50 text-white rounded-xl p-6">
              <div className="text-3xl font-sofia font-bold mb-2">
                {t("pricing.lowSeason.price")}
              </div>
              <div className="font-sofia text-white/90">{t("pricing.lowSeason.title")}</div>
              <div className="font-sofia text-sm text-white/75">
                {t("pricing.lowSeason.months")}
              </div>
            </div>
            <div className="text-center bg-[#81736d]/50 text-white rounded-xl p-6">
              <div className="text-3xl font-sofia font-bold mb-2">
                {t("pricing.midSeason.price")}
              </div>
              <div className="font-sofia text-white/90">{t("pricing.midSeason.title")}</div>
              <div className="font-sofia text-sm text-white/75">
                {t("pricing.midSeason.months")}
              </div>
            </div>
            <div className="text-center bg-[#81736d]/50 text-white rounded-xl p-6">
              <div className="text-3xl font-sofia font-bold mb-2">
                {t("pricing.highSeason.price")}
              </div>
              <div className="font-sofia text-white/90">{t("pricing.highSeason.title")}</div>
              <div className="font-sofia text-sm text-white/75">
                {t("pricing.highSeason.months")}
              </div>
            </div>
          </div>

          <div className="space-x-4">
            <Button
              size="lg"
              className="bg-[#81736d] hover:bg-[#81736d]"
              onClick={() => navigate("/booking")}
            >
              {t("common.bookNow")}
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-[#81736d] hover:text-white"
              onClick={() => navigate("/contact")}
            >
              {t("common.contactUs")}
            </Button>
          </div>
        </div>
      </section>
      {/* Mosaic Lightbox Modal */}
      {isMosaicOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80" onClick={closeMosaic} />

          {/* Content */}
          <div className="relative z-10 max-w-5xl w-full px-4">
            <div className="relative bg-black rounded-xl overflow-hidden">
              {/* Image */}
              <img
                src={mosaicItems[mosaicIndex].src}
                alt={mosaicItems[mosaicIndex].title}
                className="w-full h-[70vh] object-cover"
              />

              {/* Centered title overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-black/40 px-4 py-2 rounded">
                  <h2 className="text-white text-2xl md:text-3xl font-sofia font-bold text-center whitespace-pre-line">
                    {mosaicItems[mosaicIndex].title}
                  </h2>
                </div>
              </div>

              {/* Controls */}
              <button
                className="absolute top-3 right-3 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
                onClick={closeMosaic}
                aria-label="Close"
              >
                ✕
              </button>
              <button
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-3"
                onClick={() => setMosaicIndex((mosaicIndex - 1 + mosaicItems.length) % mosaicItems.length)}
                aria-label="Previous"
              >
                <ChevronLeft />
              </button>
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-3"
                onClick={() => setMosaicIndex((mosaicIndex + 1) % mosaicItems.length)}
                aria-label="Next"
              >
                <ChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default HomeScreen;
